import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { getAccessToken } from "@/utils/authStorage";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import styles from "./Navbar.module.css";
import SearchOverlay from "./SearchOverlay";
import XpStatus from "../XpSystem/XpStatus";
import LevelUpModal from "../XpSystem/LevelUpModal";
import { useMediaQuery } from "react-responsive";
import {
  readVipStatusCache,
  writeVipStatusCache,
  withVipStatusRequestLock,
  resolveVipStatusNamespace,
} from "../../utils/vipStatusCache";
import Sidebar from "./Sidebar";

const Navbar = () => {
  const { theme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [expanded, setExpanded] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const location = useLocation();
  const [vipStatus, setVipStatus] = useState(null);
  const [isLevelUpOpen, setIsLevelUpOpen] = useState(false);
  const [lastLevel, setLastLevel] = useState(user?.level);

  useEffect(() => {
    if (user?.level > lastLevel) {
      setIsLevelUpOpen(true);
    }
    setLastLevel(user?.level);
  }, [user?.level]);
  const API_BASE = (
    import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1"
  ).replace(/\/$/, "");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expanded && !event.target.closest(".navbar-container")) {
        setExpanded(false);
      }
    };

    if (expanded) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [expanded]);

  const fetchVipStatus = useCallback(async () => {
    const cacheNamespace = resolveVipStatusNamespace({ user });
    const cached = readVipStatusCache({ namespace: cacheNamespace });

    if (cached) {
      setVipStatus(cached);
      return;
    }

    await withVipStatusRequestLock({ namespace: cacheNamespace }, async () => {
      try {
        const BASE_URL = API_BASE;
        const token = getAccessToken();
        const response = await fetch(`${BASE_URL}/vip/status`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (response.ok) {
          const data = await response.json();
          setVipStatus(data.status);
          writeVipStatusCache({
            value: data.status,
            namespace: cacheNamespace,
          });
        } else if (response.status === 401) {
          setVipStatus(null);
          writeVipStatusCache({
            value: null,
            namespace: cacheNamespace,
          });
        } else if (response.status === 429) {
          const cachedFallback = readVipStatusCache({
            namespace: cacheNamespace,
          });
          if (cachedFallback) {
            setVipStatus(cachedFallback);
          }
        }
      } catch (error) {
        console.error("Error fetching VIP status:", error);
      }
    });
  }, [user]);

  //Fetch vip status
  useEffect(() => {
    if (isAuthenticated) {
      fetchVipStatus();
    }
  }, [isAuthenticated, fetchVipStatus]);

  return (
    <>
      <nav className={`${styles.navbar} ${theme === "dark" ? styles.dark : ""} ${theme}`}>
        <div className={`${styles.container} navbar-container`}>
          <div className={styles.leftSection}>
            {/* Hamburger Button always on the left */}
            <button
              type="button"
              className={styles.toggleButton}
              onClick={() => setExpanded(!expanded)}
              aria-label="Toggle navigation"
            >
              <span className={`${styles.toggleBar} ${expanded ? styles.bar1 : ""}`}></span>
              <span className={`${styles.toggleBar} ${expanded ? styles.bar2 : ""}`}></span>
              <span className={`${styles.toggleBar} ${expanded ? styles.bar3 : ""}`}></span>
            </button>

            <Link to="/" className={styles.logoContainer}>
              <video className={styles.logoVideo} autoPlay muted loop playsInline disablePictureInPicture>
                <source src="videos/pixel.mp4" type="video/mp4" />
              </video>
            </Link>
          </div>

          <div className={styles.navRight}>
            <button
              type="button"
              className={styles.searchIcon}
              onClick={() => setShowSearchOverlay(true)}
              aria-label="Open search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
            </button>

            <div className={styles.themeToggleWrapper}>
              <ThemeToggle />
            </div>

            {isAuthenticated ? (
              <div className="flex items-center justify-center gap-[0.75rem]">
                <XpStatus
                  xp={user?.xp || 0}
                  level={user?.level || 1}
                  xpProgress={user?.xpProgress || 0}
                />
              </div>
            ) : (
              <Link to="/login" className={styles.loginButton}>Sign In</Link>
            )}
          </div>
        </div>
      </nav>

      {/* SIDEBAR COMPONENT */}
      <Sidebar vipStatus={vipStatus} expanded={expanded} setExpanded={setExpanded} isMobile={isMobile} />

      {/* Backdrop Overlay */}
      {expanded && <div className={styles.mobileOverlay} onClick={() => setExpanded(false)} />}

      <SearchOverlay isOpen={showSearchOverlay} onClose={() => setShowSearchOverlay(false)} />
      <LevelUpModal isOpen={isLevelUpOpen} level={user?.level} onClose={() => setIsLevelUpOpen(false)} />
    </>
  );
};

export default Navbar;
